import React, { useCallback, useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import Icon from "./icons";
import { clearSession } from "../../utils/api";
import {
  ACTIVE_KEY,
  COUNTDOWN_S,
  IDLE_MS,
  isSharedComputer,
  isTestChatBusy,
} from "../../utils/sharedComputer";
import "./StillThereCard.css";

// F19: on a computer the student marked "Shared computer" at sign-in, 10 minutes with no
// typing, clicking or scrolling brings up "Still there?" with a one-minute countdown. Nobody
// answers: sign out and go to the start page, so the next class doesn't land in this
// student's account. Never while a test chat is open or answers are still saving.

const ACTIVITY_EVENTS = ["pointerdown", "keydown", "wheel", "touchstart", "scroll", "mousemove"];
// localStorage writes are throttled; other tabs read the time to keep this one awake too
const SHARE_EVERY_MS = 15000;

const readShared = () => {
  try {
    return Number(localStorage.getItem(ACTIVE_KEY)) || 0;
  } catch {
    return 0;
  }
};

const homeFor = (pathname) => (pathname.startsWith("/admin") ? "/admin/login" : "/");

export const signOutNow = () => {
  const target = homeFor(window.location.pathname);
  clearSession();
  window.location.href = target;
};

const SharedComputerGuard = ({ idleMs = IDLE_MS, countdown = COUNTDOWN_S, onSignOut = signOutNow }) => {
  const lastActive = useRef(Date.now());
  const lastShared = useRef(0);
  const [deadline, setDeadline] = useState(null);
  const [left, setLeft] = useState(countdown);
  const showing = deadline !== null;
  const showingRef = useRef(false);
  showingRef.current = showing;

  const markActive = useCallback(() => {
    if (showingRef.current) return; // only "I'm still here" closes the card
    const now = Date.now();
    lastActive.current = now;
    if (now - lastShared.current >= SHARE_EVERY_MS) {
      lastShared.current = now;
      try {
        localStorage.setItem(ACTIVE_KEY, String(now));
      } catch {
        // Storage blocked: this tab still counts its own activity
      }
    }
  }, []);

  useEffect(() => {
    const opts = { passive: true, capture: true };
    ACTIVITY_EVENTS.forEach((name) => window.addEventListener(name, markActive, opts));
    return () => ACTIVITY_EVENTS.forEach((name) => window.removeEventListener(name, markActive, opts));
  }, [markActive]);

  useEffect(() => {
    const tick = () => {
      const now = Date.now();
      if (!isSharedComputer()) {
        if (showingRef.current) setDeadline(null);
        lastActive.current = now;
        return;
      }
      const elsewhere = readShared();
      if (showingRef.current) {
        // Someone used Anna in another tab of this browser after the card came up
        if (elsewhere > lastActive.current) {
          lastActive.current = elsewhere;
          setDeadline(null);
        }
        return;
      }
      if (isTestChatBusy()) {
        lastActive.current = now;
        return;
      }
      const latest = Math.max(lastActive.current, elsewhere);
      if (now - latest >= idleMs) {
        lastActive.current = latest;
        setLeft(countdown);
        setDeadline(now + countdown * 1000);
      }
    };
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [idleMs, countdown]);

  // The countdown itself
  useEffect(() => {
    if (deadline === null) return undefined;
    let done = false;
    let id = null;
    const step = () => {
      if (done) return;
      const remaining = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
      setLeft(remaining);
      if (remaining <= 0) {
        // Once per card, even if a tick lands before the card has closed
        done = true;
        if (id !== null) window.clearInterval(id);
        lastActive.current = Date.now();
        setDeadline(null);
        onSignOut();
      }
    };
    step();
    if (!done) id = window.setInterval(step, 250);
    return () => {
      done = true;
      if (id !== null) window.clearInterval(id);
    };
  }, [deadline, onSignOut]);

  const stay = () => {
    const now = Date.now();
    lastActive.current = now;
    lastShared.current = now;
    try {
      localStorage.setItem(ACTIVE_KEY, String(now));
    } catch {
      // Same as markActive
    }
    setDeadline(null);
  };

  const signOut = () => {
    setDeadline(null);
    onSignOut();
  };

  const ring = 2 * Math.PI * 34;
  const share = Math.max(0, Math.min(1, left / countdown));

  return (
    <Modal
      show={showing}
      onHide={stay}
      backdrop="static"
      keyboard={false}
      centered
      dialogClassName="st-dialog"
      aria-labelledby="still-there-title"
    >
      <Modal.Header>
        <Modal.Title id="still-there-title">Still there?</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="am-intro st-intro">
          <span className="st-clock" aria-hidden="true">
            <svg viewBox="0 0 80 80" className="st-ring">
              <circle cx="40" cy="40" r="34" className="st-ring-track" />
              <circle
                cx="40"
                cy="40"
                r="34"
                className="st-ring-fill"
                strokeDasharray={ring}
                strokeDashoffset={ring * (1 - share)}
              />
            </svg>
            <span className="st-count">{left}</span>
          </span>
          <p className="am-intro-text st-text" aria-live="polite">
            This is a shared computer, so Anna will sign you out in{" "}
            <b>{left === 1 ? "1 second" : `${left} seconds`}</b> to keep your account safe.
          </p>
          <p className="st-saved">
            <Icon name="check" size={18} aria-hidden="true" />
            Your answers are saved.
          </p>
        </div>
        <div className="st-actions">
          <button type="button" className="am-btn is-primary is-block is-large" onClick={stay} autoFocus>
            I'm still here
          </button>
          <button type="button" className="am-btn is-soft is-block" onClick={signOut}>
            <Icon name="sign-out" size={18} />
            Sign me out
          </button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default SharedComputerGuard;
